import moment from "moment-timezone";

/**
 * The time zone for Indian Standard Time (IST).
 * @constant {string}
 */
export const IST_TIMEZONE = "Asia/Kolkata";

/**
 * A mapping of registration end datetimes for each year in Indian Standard Time (IST).
 * Use full ISO datetime strings (e.g. "2025-07-13T18:00:00") for more precise control.
 * If only a date is provided, it will default to end of that day (23:59:59).
 *
 * @constant {Object.<number, string>}
 */
export const REGISTRATION_ENDTIME = {
  2025: "2025-06-30T23:59:59",
  // Kept in step with SUBMISSION_ENDTIME below: the Dashboard registration
  // popup is the only self-service way to become a participant, and a student
  // must be a participant to submit. Closing registration before submissions
  // close strands anyone who has not registered yet.
  2026: "2026-09-02T11:59:59",
};

/**
 * Checks if the current IST time is before the registration end time for the given year.
 *
 * @param {number} year - The year for which registration end time is checked.
 * @returns {boolean} True if registration is still open, false otherwise.
 */
export const SUBMISSION_STARTTIME = {
  2026: "2026-06-22T00:00:00",
};

export const SUBMISSION_ENDTIME = {
  // Full ISO datetime is required here: isSubmissionOpen does not apply the
  // end-of-day defaulting that isRegistrationOpen does, so a bare date would
  // close submissions at the START of that day.
  2026: "2026-09-02T11:59:59",
};

export const isSubmissionOpen = (year) => {
  const startStr = SUBMISSION_STARTTIME[year];
  const endStr = SUBMISSION_ENDTIME[year];
  if (!startStr || !endStr) return false;
  const now = moment.tz(IST_TIMEZONE);
  return (
    now.isAfter(moment.tz(startStr, IST_TIMEZONE)) &&
    now.isBefore(moment.tz(endStr, IST_TIMEZONE))
  );
};

export const isRegistrationOpen = (year) => {
  const targetDateStr = REGISTRATION_ENDTIME[year];

  if (!targetDateStr) {
    return false; // No festival planned for this year
  }

  let targetTime = moment.tz(targetDateStr, IST_TIMEZONE);

  // If only date was provided, default to end of the day
  if (targetDateStr.length <= 10) {
    targetTime = targetTime.endOf("day");
  }

  const currentTime = moment.tz(IST_TIMEZONE);
  return currentTime.isBefore(targetTime);
};

/**
 * Reads the Tinkerpreneur edition year out of a signup URL's `source` param.
 *
 * Returns null when the link is not a Tinkerpreneur one, so callers can treat a
 * missing or unrecognised source exactly as they did before this existed. The
 * year is parsed rather than string-matched so a new edition needs no code
 * change here.
 *
 * @param {string} search - location.search, e.g. "?source=tinkerpreneur-2026"
 * @returns {number|null}
 */
export const parseTinkerpreneurYear = (search) => {
  const source = new URLSearchParams(search).get("source");
  const match = /^tinkerpreneur-(\d{4})$/.exec(source || "");
  return match ? Number(match[1]) : null;
};

/**
 * Human-readable close time for an edition, or null when the edition has no
 * configured date — which is also how callers detect that it does not exist.
 *
 * Format matches ATLTinkerpreneurDeadlineNotice so the two never disagree.
 * REGISTRATION_ENDTIME values are timezone-naive IST wall-clock strings, so
 * plain moment() is used here (not moment.tz) to preserve the digits as written.
 *
 * @param {number} year
 * @returns {string|null}
 */
export const getRegistrationCloseLabel = (year) => {
  const endStr = REGISTRATION_ENDTIME[year];
  return endStr ? moment(endStr).format("Do MMMM YYYY, h:mm A") : null;
};
